import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";
import Dropzone from "dropzone";

Dropzone.autoDiscover = false;

// Controls a dropzone with direct uploads and no submit:
//
//   1. This controller connects and initializes dropzone.
//   2. The user drags-and-drops (or clicks) one or more files in the area.
//   3. The area's message changes, the files are added to dropzone, and a
//      direct upload starts (i.e., as many parallel uploads as files).
//   4. When a direct upload succeeds, a hidden input is added to the form.
//   5. When all the uploads are completed, a "finished" event is dispatched.
//
// Note, there is no submit. That's up to the container.
//
// Also, some HTML attributes are required (e.g., multiple) or easily accessible
// (e.g., data-direct-upload-url) on the file input. Instead of duplicating them
// as Stimulus values, they're read from the HTML.
export default class extends Controller {
  static targets = ["area", "input", "text"];
  static values = {
    maxFiles: { type: Number, default: 10 },
    maxFileSize: { type: Number, default: 25 }, // MB
    acceptedFiles: {
      type: String,
      default:
        "image/*,application/pdf,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation",
    },
    uploadingClass: { type: String, default: "animate-pulse" },
  };

  #dropzone;
  #activeUploads;
  #totalUploads;
  #submitting = false;

  connect() {
    this.#activeUploads = 0;
    this.#totalUploads = 0;

    this.#dropzone = new Dropzone(this.areaTarget, {
      url: this.#url, // Required for Dropzone but not used, as we manually manage uploads.
      maxFiles: this.#multiple ? this.maxFilesValue : 1,
      maxFileSize: this.maxFileSizeValue,
      acceptedFiles: this.acceptedFilesValue,
      autoProcessQueue: false,
      parallelUploads: 3,
      disablePreviews: true,
    });

    this.#dropzone.on("addedfile", (file) => this.#add(file));

    this.#form?.addEventListener("turbo:submit-start", this.#submitStarted);
    this.#form?.addEventListener("turbo:submit-end", this.#submitEnded);
  }

  disconnect() {
    this.#form?.removeEventListener("turbo:submit-start", this.#submitStarted);
    this.#form?.removeEventListener("turbo:submit-end", this.#submitEnded);

    this.#dropzone?.destroy();
  }

  /* Private */

  get #url() {
    return this.hasInputTarget && this.inputTarget.dataset.directUploadUrl;
  }

  get #multiple() {
    return this.hasInputTarget && this.inputTarget.multiple;
  }

  get #single() {
    return !this.#multiple;
  }

  // A stimulus target won't work, as the form may not be a descendant.
  get #form() {
    return this.inputTarget.closest("form");
  }

  #submitStarted = () => {
    this.#submitting = true;
  };

  #submitEnded = () => {
    this.#submitting = false;
  };

  // Dropzone reports every file added, even past `maxFiles`, so a second file
  // dropped while a self-submitting form is busy would go out as a second request.
  #add(file) {
    if (this.#single && (this.#activeUploads > 0 || this.#submitting)) {
      this.#dropzone.removeFile(file);
      return;
    }

    this.#totalUploads++;
    this.#activeUploads++;

    this.#notify(file);

    this.#upload(file);
  }

  #notify(file) {
    this.uploadingClassValue.split(" ").forEach((c) => this.areaTarget.classList.add(c));

    const message = this.#multiple
      ? `Uploading ${this.#totalUploads} files...`
      : `Uploading file...`;

    if (this.hasTextTarget) {
      this.textTarget.textContent = message;
    } else {
      this.areaTarget.textContent = message;
    }
  }

  #upload(file) {
    const upload = new DirectUpload(file, this.#url, this);

    upload.create((error, blob) => {
      this.#activeUploads--;

      error ? this.#error(file, error) : this.#success(file, blob);

      this.#notify(file);

      this.#finish();
    });
  }

  #error(file, error) {
    this.#dropzone.emit("error", file, error);

    console.log(error);
  }

  #success(file, blob) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = this.inputTarget.name; // The file input has the single or multiple name we need.
    input.value = blob.signed_id;

    this.#form.appendChild(input);

    this.#dropzone.emit("success", file);
    this.#dropzone.emit("complete", file);
  }

  #finish() {
    if (this.#activeUploads > 0) return;

    this.#totalUploads = 0;

    this.dispatch("finished", { detail: { files: this.#dropzone.files } });
  }
}
